import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { getRepo, isEmail, isDemoMode, type Role, type TeamMember } from '../../lib/repo';
import { useAsync } from '../../lib/hooks';
import { roleRank } from '../../shared/status.js';
import {
  Badge, Button, Card, CardHeader, Dialog, EmptyState, Field, Input, Select, Spinner, cn, useToast,
} from '../../components/ui';

const ROLE_OPTIONS: { value: Role; label: string }[] = [
  { value: 'admin', label: 'Administrator' },
  { value: 'editor', label: 'Editor' },
  { value: 'writer', label: 'Writer' },
  { value: 'analyst', label: 'Analyst' },
  { value: 'social_manager', label: 'Social media manager' },
  { value: 'seo_manager', label: 'SEO manager' },
];

export function TeamPage() {
  const { roles } = useAuth();
  const toast = useToast();
  const isAdmin = roles.includes('admin');
  const { data, loading, reload } = useAsync<TeamMember[]>(() => getRepo().listTeam(), []);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('writer');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [removeTarget, setRemoveTarget] = useState<TeamMember | null>(null);
  const [removeError, setRemoveError] = useState('');
  const [removing, setRemoving] = useState(false);

  const members = [...(data ?? [])].sort((a, b) => roleRank(b.role) - roleRank(a.role) || a.name.localeCompare(b.name));

  async function submitInvite(e: FormEvent) {
    e.preventDefault();
    setError('');
    if (!isEmail(email)) { setError('Enter a valid email address.'); return; }
    setBusy(true);
    try {
      await getRepo().inviteMember(email.trim().toLowerCase(), role);
      toast(`Invite sent to ${email}`);
      setOpen(false);
      setEmail('');
      setRole('writer');
      reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the invite.');
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove() {
    if (!removeTarget) return;
    setRemoving(true);
    setRemoveError('');
    try {
      await getRepo().removeMember(removeTarget.userId, removeTarget.role);
      toast(`Removed ${removeTarget.name}`);
      setRemoveTarget(null);
      reload();
    } catch (err) {
      setRemoveError(err instanceof Error ? err.message : 'Could not remove this member.');
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold">Team &amp; roles</h1>
          <p className="mt-1 text-sm text-muted">Who can sign in to the admin, and what they may do.</p>
        </div>
        <Button onClick={() => setOpen(true)}>Invite member</Button>
      </header>

      <Card>
        <CardHeader
          title="Members"
          description="Roles are enforced by row-level security and back-end checks — hiding a button is not the control."
        />
        {loading ? <Spinner label="Loading team…" /> : null}
        {!loading && members.length === 0 ? <EmptyState title="No members yet" description="Invite your first colleague to get started." /> : null}
        {members.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-5 py-3 font-medium">Member</th>
                  <th className="px-5 py-3 font-medium">Email</th>
                  <th className="px-5 py-3 font-medium">Role</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {members.map((m) => (
                  <tr key={`${m.userId}-${m.role}`}>
                    <td className="px-5 py-3 font-medium">{m.name}</td>
                    <td className="px-5 py-3 text-muted">{m.email || '—'}</td>
                    <td className="px-5 py-3"><Badge tone={m.role}>{m.role.replace('_', ' ')}</Badge></td>
                    <td className="px-5 py-3"><Badge tone={m.status}>{m.status}</Badge></td>
                    <td className="px-5 py-3 text-right">
                      <button
                        type="button"
                        disabled={!isAdmin}
                        title={isAdmin ? `Remove ${m.name}` : 'Only administrators can remove members'}
                        onClick={() => { setRemoveError(''); setRemoveTarget(m); }}
                        className={cn(
                          'rounded-md border px-2.5 py-1 text-xs',
                          isAdmin ? 'border-danger/40 text-danger hover:bg-danger/10' : 'cursor-not-allowed border-border text-muted/60',
                        )}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>

      <p className="text-xs text-muted">
        Permission checks live in Postgres RLS (<code>has_role()</code>) and are mirrored in the UI only for convenience.
        Invites are recorded in the audit log.{isDemoMode ? ' Running in demo mode: invites are kept in memory for this session.' : ''}
      </p>

      <Dialog open={open} onClose={() => setOpen(false)} title="Invite a team member">
        <form className="space-y-4" onSubmit={submitInvite} noValidate>
          <Field label="Email address" error={error}>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="colleague@thestoryline.xyz" required />
          </Field>
          <Field label="Role" hint="Start from the least privilege that lets them do the job.">
            <Select value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Send invite'}</Button>
          </div>
        </form>
      </Dialog>

      <Dialog open={removeTarget !== null} onClose={() => setRemoveTarget(null)} title="Remove team member">
        <p className="text-sm text-muted">
          Remove <strong>{removeTarget?.name}</strong> ({removeTarget?.role.replace('_', ' ')})? They immediately lose access
          to the admin. Their articles and revisions are kept.
        </p>
        {removeError ? <p className="mt-3 text-sm text-danger">{removeError}</p> : null}
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="outline" onClick={() => setRemoveTarget(null)} disabled={removing}>Cancel</Button>
          <Button variant="danger" onClick={() => void confirmRemove()} disabled={removing}>
            {removing ? 'Removing…' : 'Remove'}
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
